import {
  PlainButton,
  RenderTooltipTriggerProps,
  Tooltip,
} from "@jugl-web/ui-components";
import { cx } from "@jugl-web/utils";
import { FC, ReactElement, ReactNode } from "react";
import { collapsedIconButtonClassName } from "../../../../consts";
import { ReactComponent as AddIcon } from "./assets/add.svg";

export const SidebarSectionHeader: FC<{
  title: string;
  isExpanded: boolean;
  onAdd: () => void;
  addButtonTooltip: ReactNode;
  showAddButton?: boolean;
  /** When true, the add button is styled disabled and does not trigger `onAdd`. */
  isAddDisabled?: boolean;
  /**
   * Renders a custom tooltip around the (disabled) add button — e.g. one that
   * explains why adding is blocked. Receives the tooltip trigger renderer.
   */
  renderDisabledTooltip?: (
    renderTrigger: (props: RenderTooltipTriggerProps) => ReactElement
  ) => ReactElement;
}> = ({
  title,
  isExpanded,
  onAdd,
  addButtonTooltip,
  showAddButton = true,
  isAddDisabled = false,
  renderDisabledTooltip,
}) => {
  if (!showAddButton && !isExpanded) return null;

  const renderTriggerButton =
    (className: string) =>
    ({ props, ref }: RenderTooltipTriggerProps) => (
      <PlainButton
        ref={ref}
        {...props}
        onClick={isAddDisabled ? undefined : onAdd}
        className={cx(
          className,
          isAddDisabled && "cursor-not-allowed opacity-40"
        )}
      >
        <AddIcon />
      </PlainButton>
    );

  const addButton = (className: string) =>
    isAddDisabled && renderDisabledTooltip ? (
      renderDisabledTooltip(renderTriggerButton(className))
    ) : (
      <Tooltip placement="right" renderTrigger={renderTriggerButton(className)}>
        {addButtonTooltip}
      </Tooltip>
    );

  if (!isExpanded) {
    return addButton(cx(collapsedIconButtonClassName, "mx-auto h-8 w-8"));
  }

  return (
    <div className="flex items-center justify-between gap-1.5 px-2 py-0.5">
      <span className="font-secondary text-sm font-medium text-black">
        {title}
      </span>
      {showAddButton && addButton(cx(collapsedIconButtonClassName, "h-6 w-6"))}
    </div>
  );
};
